import {ApiObject} from "./api"
import {ApiObjectFields, EOFields, EOFieldsWithParent,
  Guid, ItemCode, WarehouseCode,
} from "./types"
import {TypeMap, assertTM, copyFilterEOValueToUpdateValue,
  GuidTypeMap, NullableStringTypeMap, StringTypeMap, NumberTypeMap, ISODateTypeMap,
  NullableNumberTypeMap, ItemCodeTypeMap, WarehouseCodeTypeMap
} from "./generaltypemaps"
import Enum from "enum-typescript"
import {getPlannedSalesReturnEOURL} from "../links"
import {ISODate} from "../date"

export class PSRStatus extends Enum<number> {
  static OPEN = PSRStatus.createEnum(20)
  static CONFIRMED = PSRStatus.createEnum(30)
  static PROCESSED = PSRStatus.createEnum(50)
}

export const PSRStatusTypeMap: TypeMap<PSRStatus> = copyFilterEOValueToUpdateValue({
  fromEO: (data: number) => PSRStatus.valueOf(assertTM(
    d => PSRStatus.valueOf(d) !== undefined, data)),
  toEOFilterValue: (data: PSRStatus) => data.value.toString(),
})

export const PlannedSalesReturnFields = {
  plannedSalesReturnID: GuidTypeMap,
  returnNumber: NumberTypeMap,
  returnDate: ISODateTypeMap,
  status: PSRStatusTypeMap,
  warehouseCode: WarehouseCodeTypeMap,
  warehouseDescription: StringTypeMap,
}

export const PlannedSalesReturnLineFields = {
  iD: GuidTypeMap,
  itemCode: ItemCodeTypeMap,
  itemDescription: StringTypeMap,
  notes: NullableStringTypeMap,
  plannedReturnQuantity: NumberTypeMap,
  receivedQuantity: NullableNumberTypeMap,
  lineNumber: NumberTypeMap,
}


export class PlannedSalesReturn extends ApiObject
implements ApiObjectFields<typeof PlannedSalesReturnFields> {
  readonly plannedSalesReturnID: Guid
  readonly returnNumber: number
  readonly returnDate: ISODate
  readonly status: PSRStatus
  readonly warehouseCode: WarehouseCode
  readonly warehouseDescription: string

  readonly plannedSalesReturnLines: PlannedSalesReturnLine[]

  constructor(props: EOFields<typeof PlannedSalesReturn>) {
    super({})
    this.plannedSalesReturnID =
      PlannedSalesReturn.FIELDS.plannedSalesReturnID.fromEO(
        props.PlannedSalesReturnID)
    this.returnNumber = PlannedSalesReturn.FIELDS.returnNumber.fromEO(
      props.ReturnNumber)
    this.returnDate = PlannedSalesReturn.FIELDS.returnDate.fromEO(props.ReturnDate)
    this.status = PlannedSalesReturn.FIELDS.status.fromEO(props.Status)
    this.warehouseCode = PlannedSalesReturn.FIELDS.warehouseCode.fromEO(
      props.WarehouseCode)
    this.warehouseDescription =
      PlannedSalesReturn.FIELDS.warehouseDescription.fromEO(
        props.WarehouseDescription)

    this.plannedSalesReturnLines = props.PlannedSalesReturnLines.map(
      eoPSRL => new PlannedSalesReturnLine({...eoPSRL, Parent: this}))
  }

  static get URL(): string {
    return "salesorder/PlannedSalesReturns"
  }

  static get FIELDS(): typeof PlannedSalesReturnFields {
    return PlannedSalesReturnFields
  }

  static get EXPANDFIELDS(): typeof PlannedSalesReturnExpandFields {
    return PlannedSalesReturnExpandFields
  }

  getExactOnlineUrl(division: number): URL {
    return getPlannedSalesReturnEOURL(division, this.plannedSalesReturnID)
  }

  getId(): Guid {
    return this.plannedSalesReturnID
  }
}

export class PlannedSalesReturnLine extends ApiObject
    implements ApiObjectFields<typeof PlannedSalesReturnLineFields> {
  readonly parent: PlannedSalesReturn

  readonly iD: Guid
  readonly itemCode: ItemCode
  readonly itemDescription: string
  readonly notes: string | null
  readonly plannedReturnQuantity: number
  readonly receivedQuantity: number | null
  readonly lineNumber: number

  constructor(props: EOFieldsWithParent<typeof PlannedSalesReturnLine>) {
    super({})
    this.iD = PlannedSalesReturnLine.FIELDS.iD.fromEO(props.ID)
    this.itemCode = PlannedSalesReturnLine.FIELDS.itemCode.fromEO(props.ItemCode)
    this.itemDescription = PlannedSalesReturnLine.FIELDS.itemDescription.fromEO(
      props.ItemDescription)
    this.notes = PlannedSalesReturnLine.FIELDS.notes.fromEO(props.Notes)
    this.plannedReturnQuantity =
      PlannedSalesReturnLine.FIELDS.plannedReturnQuantity.fromEO(
        props.PlannedReturnQuantity)
    this.receivedQuantity = PlannedSalesReturnLine.FIELDS.receivedQuantity.fromEO(
      props.ReceivedQuantity)
    this.lineNumber = PlannedSalesReturnLine.FIELDS.lineNumber.fromEO(
      props.LineNumber)

    this.parent = props.Parent
  }

  static get FIELDS(): typeof PlannedSalesReturnLineFields {
    return PlannedSalesReturnLineFields
  }

  get quantity() {
    return this.plannedReturnQuantity;
  }
}

const PlannedSalesReturnExpandFields = {
  plannedSalesReturnLines: PlannedSalesReturnLine
} as const
